import { getFormattedCustomMetadataValue } from "helpers/utility/customMetadataHelpers";
import { getTextWidth, copyToClipboard } from "helpers/utility/utilities";
import { formatUnderscoreWithSpace } from "helpers/utility/formatters";
import { getImageCustomMetadata } from "redux/SingleImage/selectors";
import { useCallback, useEffect, useRef, useState } from "react";
import VLAccordian from "views/components/Accordian";
import { ImageCustomMetadata } from "types";
import { useSelector } from "react-redux";
import classNames from "classnames";

import UserTagIcon from "assets/icons/UserTagIcon";
import LinkIcon from "assets/icons/LinkIcon";
import styles from "./style.module.scss";
import { Typography } from "antd";
import VLTooltip from "views/uikit/VLTooltip";

interface FieldProps {
  field: ImageCustomMetadata;
}

const Field = ({ field }: FieldProps) => {
  const [copiedText, setCopiedText] = useState("");
  const formattedTitle = formatUnderscoreWithSpace(field.field_name);
  const formattedValue = getFormattedCustomMetadataValue(field);
  const { Link, Paragraph } = Typography;

  const isLinkField = field.field_type === "link";

  const handleCopyUrlClick = (url: string) => {
    copyToClipboard(url)
      .then(() => {
        setCopiedText(url);
        setTimeout(() => setCopiedText(""), 3000);
      })
      .catch((err) => console.error(err));
  };

  const copyIconClassnames = classNames(styles.copyIcon, {
    [styles.defaultCursor]: copiedText,
  });

  // For link fields, we need to handle both single links and arrays of links
  const renderLinkField = () => {
    const links = Array.isArray(field.value) ? field.value : [field.value];

    return (
      <div className={styles.linkFieldContainer}>
        {links.map((link: string, index: number) => (
          <div
            key={`${field.field_name}-link-${index}`}
            className={styles.linkRow}
          >
            <VLTooltip title={link}>
              <Link href={link} target="_blank" className={styles.linkText}>
                {link}
              </Link>
            </VLTooltip>
            <VLTooltip title={copiedText === link ? "Copied!" : "Copy URL"}>
              <div
                className={copyIconClassnames}
                onClick={() => handleCopyUrlClick(link)}
              >
                <LinkIcon clicked={copiedText === link} />
              </div>
            </VLTooltip>
          </div>
        ))}
      </div>
    );
  };

  const renderRegularField = () => (
    <Paragraph
      className={styles.description}
      ellipsis={{ rows: 3, tooltip: true }}
    >
      {formattedValue}
    </Paragraph>
  );

  return (
    <div
      className={classNames(styles.infoRow, {
        [styles.linkInfoRow]: isLinkField,
      })}
    >
      <span className={styles.title} title={formattedTitle}>
        {formattedTitle}
      </span>
      {isLinkField ? renderLinkField() : renderRegularField()}
    </div>
  );
};

const CustomMetadataBox = () => {
  const imageCustomMetadata = useSelector(getImageCustomMetadata);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateAndSetTitleWidth = useCallback(() => {
    if (!containerRef.current) return;

    // Calculate the width of the longest title
    const maxTitleWidth = imageCustomMetadata.reduce(
      (max: number, field) =>
        Math.max(
          max,
          getTextWidth(formatUnderscoreWithSpace(field.field_name), "13px"),
        ),
      0,
    );

    // Add some padding to account for font rendering differences and ensure no truncation
    const paddedTitleWidth = maxTitleWidth + 8;

    // Get container width and calculate max allowed title width (50% minus gap)
    const containerWidth = containerRef.current.offsetWidth - 20; // Subtract padding
    const maxAllowedTitleWidth = containerWidth / 2 - 9; // 9px is half the gap

    // Use the smaller of the two values
    const finalTitleWidth = Math.min(paddedTitleWidth, maxAllowedTitleWidth);

    // Set CSS custom property for consistent title width
    containerRef.current.style.setProperty(
      "--title-width",
      `${finalTitleWidth}px`,
    );
  }, [imageCustomMetadata]);

  useEffect(() => {
    calculateAndSetTitleWidth();

    // Use ResizeObserver for proper resize handling
    const resizeObserver = new ResizeObserver(() => {
      calculateAndSetTitleWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [imageCustomMetadata, calculateAndSetTitleWidth]);

  return imageCustomMetadata.length > 0 ? (
    <div className={styles.customMetadataBox}>
      <div className={styles.infoContainer} ref={containerRef}>
        {imageCustomMetadata.map((field) => (
          <Field field={field} key={field.field_name} />
        ))}
      </div>
    </div>
  ) : (
    <noscript />
  );
};

const CustomMetadataWrapper = () => {
  const imageCustomMetadata = useSelector(getImageCustomMetadata);
  if (imageCustomMetadata.length === 0) return <noscript />;
  return (
    <VLAccordian
      key="Custom Metadata"
      title="Custom Metadata"
      icon={<UserTagIcon />}
      className={styles.imagePageAccordian}
    >
      <CustomMetadataBox />
    </VLAccordian>
  );
};
export default CustomMetadataWrapper;
